import React, { useMemo } from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    LabelList,
} from "recharts";
import type { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";

export type BackendItemDelivered = {
    id: string;
    name: string;
    totalQuantity: number;
};

export type DeliveredProps = {
    title?: string;
    periodLabel: string;
    items?: BackendItemDelivered[];
    loading?: boolean;
    error?: string | null;
    height?: number;
    className?: string;
    color?: string;
    maxRows?: number;
};

const fmtU = (n: number) => new Intl.NumberFormat("es-ES").format(n);

export const DeliveredToSuppliersRanking: React.FC<DeliveredProps> = ({
                                                                          title = "Cantidad de productos entregados a proveedores",
                                                                          periodLabel,
                                                                          items = [],
                                                                          loading = false,
                                                                          error = null,
                                                                          height = 320,
                                                                          className = "",
                                                                          color = "#6366f1",
                                                                          maxRows = 6,
                                                                      }) => {
    const data = useMemo(() => {
        const parsed = (items || []).map((it) => ({
            id: it.id,
            name: it.name,
            value: Number(it.totalQuantity) || 0,
        }));
        return parsed.sort((a, b) => b.value - a.value).slice(0, maxRows);
    }, [items, maxRows]);

    const noData = !loading && !error && data.length === 0;

    return (
        <div className={`rounded-2xl border bg-white shadow-sm ${className}`} style={{ minHeight: height }}>
            <div className="p-4 flex items-center justify-between">
                <h3 className="font-semibold">{title}</h3>
                <div className="text-sm text-gray-600">{periodLabel}</div>
            </div>

            <div className="px-4 pb-6 relative">
                {loading ? (
                    <div className="p-6 text-sm text-gray-500">Cargando ranking…</div>
                ) : error ? (
                    <div className="p-6 text-sm text-rose-600">{error}</div>
                ) : noData ? (
                    <div className="p-6 text-sm text-gray-500">No hay datos para el periodo seleccionado.</div>
                ) : (
                    <div style={{ height: height - 90 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data}
                                layout="vertical"
                                margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                                barCategoryGap={12}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    type="number"
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(v) => new Intl.NumberFormat("es-ES").format(Number(v))}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={240}
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 12 }}
                                />
                                <Tooltip
                                    cursor={{ opacity: 0.08 }}
                                    formatter={(value: ValueType, _name: NameType) => [fmtU(Number(value)), "Unidades" as NameType]}
                                />
                                <Bar dataKey="value" radius={[8, 8, 8, 8]} fill={color}>
                                    <LabelList dataKey="value" position="right" formatter={(v: any) => fmtU(Number(v))} className="text-[11px] fill-gray-700" />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};
